const KEY = "pos_active_branch"
const META_KEY = "pos_branch_meta"
const REPORT_BRANCH_KEY = "pos_report_branch"
const REPORT_BRANCH_EVENT = "pos-report-branch"
const LOCAL_DEMO_BRANCH_KEY = "pos_local_demo_branch"

type LocalDemoBranch = { id: number; name: string; mainId: number }

export const BRANCH_HOLD_MESSAGE =
  "This action is not available on this branch yet."

type BranchMeta = { mainId: number; names: Record<string, string> }

export function selectedBranchId(): string | null {
  try { return window.sessionStorage.getItem(KEY) } catch { return null }
}

export function saveSelectedBranch(id: number) {
  window.sessionStorage.setItem(KEY, String(id))
}

export function saveBranchMeta(mainId: number, branches: { id: number; name: string }[]) {
  const names: Record<string, string> = {}
  for (const branch of branches) names[String(branch.id)] = branch.name
  window.sessionStorage.setItem(META_KEY, JSON.stringify({ mainId, names } satisfies BranchMeta))
}

export function activeBranchLabel(): { id: string; name: string; secondary: boolean } | null {
  try {
    const id = window.sessionStorage.getItem(KEY)
    const raw = window.sessionStorage.getItem(META_KEY)
    if (!id || !raw) return null
    const meta = JSON.parse(raw) as BranchMeta
    if (!meta || typeof meta.mainId !== "number" || typeof meta.names !== "object") return null
    return { id, name: meta.names[id] ?? "Branch", secondary: id !== String(meta.mainId) }
  } catch {
    return null
  }
}

export function clearSelectedBranch() {
  try {
    window.sessionStorage.removeItem(KEY)
    window.sessionStorage.removeItem(META_KEY)
    window.sessionStorage.removeItem(REPORT_BRANCH_KEY)
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith(`${REPORT_BRANCH_KEY}:`)) window.sessionStorage.removeItem(key)
    }
  } catch { /* Browser storage may be disabled. */ }
}

export function reportBranchOverride(reportApi: string): string | null {
  if (!reportApi) return null
  try { return window.sessionStorage.getItem(`${REPORT_BRANCH_KEY}:${reportApi}`) } catch { return null }
}

export function saveReportBranch(reportApi: string, id: string) {
  try {
    const key = `${REPORT_BRANCH_KEY}:${reportApi}`
    if (id) window.sessionStorage.setItem(key, id)
    else window.sessionStorage.removeItem(key)
  } catch { /* Browser storage may be disabled. */ }
  window.dispatchEvent(new Event(REPORT_BRANCH_EVENT))
}

export function subscribeReportBranch(listener: () => void) {
  window.addEventListener(REPORT_BRANCH_EVENT, listener)
  return () => window.removeEventListener(REPORT_BRANCH_EVENT, listener)
}

export function pinStaffHomeBranch(user: {
  isTenantAdmin?: boolean
  features?: { multiBranch?: boolean }
  homeBranchId?: number | null
  homeBranchName?: string | null
  mainBranchId?: number | null
  tenantId?: number | null
  localDemoShop?: boolean
}) {
  if (typeof window === "undefined") return
  if (restoreLocalDemoBranch(user)) return
  if (user.isTenantAdmin || user.features?.multiBranch !== true) return
  const homeId = user.homeBranchId ?? user.mainBranchId
  if (homeId == null) return
  saveSelectedBranch(homeId)
  saveBranchMeta(user.mainBranchId ?? homeId, [{ id: homeId, name: user.homeBranchName?.trim() || "Main Branch" }])
}

export function readLocalDemoBranch(): LocalDemoBranch | null {
  try {
    const raw = window.localStorage.getItem(LOCAL_DEMO_BRANCH_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LocalDemoBranch
    if (!parsed || typeof parsed.id !== "number" || typeof parsed.name !== "string") return null
    return parsed
  } catch {
    return null
  }
}

export function saveLocalDemoBranch(branch: { id: number; name: string }, mainId: number) {
  const payload: LocalDemoBranch = { id: branch.id, name: branch.name, mainId }
  window.localStorage.setItem(LOCAL_DEMO_BRANCH_KEY, JSON.stringify(payload))
  saveSelectedBranch(branch.id)
  saveBranchMeta(mainId, [{ id: branch.id, name: branch.name }])
}

export function restoreLocalDemoBranch(user: { localDemoShop?: boolean; tenantId?: number | null }): boolean {
  if (!user.localDemoShop || user.tenantId !== 1) return false
  const saved = readLocalDemoBranch()
  if (!saved) return false
  saveSelectedBranch(saved.id)
  saveBranchMeta(saved.mainId || saved.id, [{ id: saved.id, name: saved.name }])
  return true
}
